/**
 * This method will make a GET call to the provided url using jQuery Ajax. Since method is async this
 * means that all errors thrown will appear under Promise.rejected flow. Implement your error
 * handling for this method as such.
 * @param {string} url URL to make GET call against
 * @param {function|*} [successCallback] Callback function to execute on success
 * @param {function|*} [errorCallback] Callback function to execute on failure
 * @param {number} [retries=0] Number of retries to attempt for request
 * @returns {Promise<*>} The response from the call
 */
async function _makeGetCall(url, successCallback, errorCallback, retries = 0){
    let settings = {
        url: url,
        type: 'GET',
        dataType: 'json'
    }

    return _makeHttpCall(settings, successCallback, errorCallback, retries);
}

/**
 * This method will make a call to the provided url using jQuery Ajax. Since method is async this
 * means that all errors thrown will appear under Promise.rejected flow. Implement your error
 * handling for this method as such.
 * @param {object} settings Required. Contains the necessary values to make the call via jQuery.ajax (i.e. url, method type, etc.)
 * @param {function|*} [successCallback] Callback function to execute on success
 * @param {function|*} [errorCallback] Callback function to execute on failure
 * @param {number} [retries=0] Number of retries to attempt for request
 * @returns {Promise<*>} The response from the call
 */
async function _makeHttpCall(settings, successCallback, errorCallback, retries = 0){
    // Do we have the minimum required to make a call?
    if(
        isEmpty(settings)
        || isEmpty(settings.url)
        || isEmpty(settings.type)
    ){
        throw new ReferenceError("Settings is missing required values");
    }

    // Emulating overloading method
    if(!isUndefinedOrNull(successCallback) && typeof successCallback === 'number'){
        retries = successCallback;
        successCallback = null;
    }
    else if(!isUndefinedOrNull(errorCallback) && typeof successCallback === 'number'){
        retries = errorCallback;
        errorCallback = null;
    }

    // clear callbacks if they are not functions
    if(!isFunction(successCallback)){
        successCallback = null
    }
    if(!isFunction(errorCallback)){
        errorCallback = null
    }

    settings.headers = {
        ...settings.headers,
        'Ontech-X-Rollbar-Session' : ontech.getSessionId()
    };

    if (!isEmpty(settings.contentType) && settings.contentType.includes('json')
        && !isEmpty(settings.data) && typeof settings.data !== "string"){
        settings.data = JSON.stringify(settings.data);
    }

    return $.ajax({
        ...settings,
        retryLimit: retries,
        error: function (xhr, textStatus, errorThrown) {
            if(!isUndefinedOrNull(errorCallback)){
                errorCallback(xhr, textStatus, errorThrown)
            }
            ontech.log.trace(`Error during Calling ${settings.url} using http method ${settings.type} textStatus: ${textStatus}, errorThrown: ${errorThrown}`);

            // Retries left?
            if (this.retryLimit > 0) {
                this.retryLimit--;

                // Try again
                $.ajax(this);
            } else {
                ontech.log.error(`Error during Calling ${settings.url} using http method ${settings.type} throwing error ${errorThrown}`);
                throw  xhr
            }
        },
        success: function (data, textStatus, xhr) {
            if (!isUndefinedOrNull(successCallback)) {
                successCallback(data, textStatus, xhr);
            }
        }
    })
}

/**
 * This method will make a POST call to the provided url using jQuery Ajax. Since method is async this
 * means that all errors thrown will appear under Promise.rejected flow. Implement your error
 * handling for this method as such.
 * @param url URL to make POST call against
 * @param data Data to send via the POST call
 * @param {function|*} [successCallback] Callback function to execute on success
 * @param {function|*} [errorCallback] Callback function to execute on failure
 * @param {number} [retries=0] Number of retries to attempt for request
 * @param {string} [contentType='application/x-www-form-urlencoded; charset=UTF-8'] Content type to use for Post call
 * @param {object} [headers={}] Headers to add to call
 * @returns {Promise<*>} The response from the call
 */
async function _makePostCall(
    url,
    data,
    successCallback,
    errorCallback,
    retries = 0,
    contentType= 'application/x-www-form-urlencoded; charset=UTF-8',
    headers = {}
){
    if(isEmpty(url) || isEmpty(data)){
        let source = isEmpty(url) ? 'url' : '';
        source += !isEmpty(source) ? ' and ' : ''
        source += isEmpty(data) ? 'data' : ''
        throw new ReferenceError(source + ' cannot be null or undefined')
    }

    // Emulating overloading method
    if(!isUndefinedOrNull(successCallback)){
        if(typeof successCallback === 'number'){
            retries = successCallback;
            successCallback = null;
        } else if(
            typeof successCallback === 'string'
            && MIME_TYPES.includes(successCallback.split(';')[0])
        ){
            contentType = successCallback;
            successCallback = null;
        } else if (typeof  successCallback === 'object') {
            headers = successCallback;
            successCallback = null;
        } else if(!isFunction(successCallback)){
            ontech.log.warn('Could not determine successCallback overload usage, setting to null', successCallback);
            successCallback = null
        }
    }
    if(!isUndefinedOrNull(errorCallback)){
        if(typeof errorCallback === 'number'){
            retries = errorCallback;
            errorCallback = null;
        } else if(
            typeof errorCallback === 'string'
            && MIME_TYPES.includes(errorCallback)
        ){
            contentType = errorCallback;
            errorCallback = null;
        } else if (typeof  errorCallback === 'object') {
            headers = errorCallback;
            errorCallback = null;
        } else if(!isFunction(errorCallback)){
            ontech.log.warn('Could not determine errorCallback overload usage, setting to null', successCallback);
            errorCallback = null
        }
    }
    if(typeof retries === 'string'){
        if(MIME_TYPES.includes(retries)) {
            contentType = retries;
            retries = 0;
        } else {
            ontech.log.warn('Could not determine retries overload usage, setting to default', retries);
            retries = 0;
        }
    } else if (typeof  retries === 'object') {
        headers = retries;
        retries = 0;
    }

    let settings = {
        type: 'POST',
        url: url,
        contentType: contentType,
        data: data,
        dataType: 'json',
        headers: headers
    };

    return _makeHttpCall(settings, successCallback, errorCallback, retries)
}

/**
 * This method will replace the text of an element who currently has the provided text to replace
 * @param {string|JQuery} element Element to check
 * @param {string} from Text to replace
 * @param {string} to Text to replace it with
 * @param {boolean} [ignoreCase=true] Should case matter
 * @param {boolean} [stripAndTrim=false] Should it strip all new lines and trim text
 */
const changeTextToElement = (element, from, to, ignoreCase = true, stripAndTrim = false) => {
    $(element).each(function () {
        let text;
        const $el = $(this);
        text = selfOrDefault($el.text(), '');

        if(stripAndTrim){
            text = text.replaceAll('\n', '').trim();
        }

        if(
            (ignoreCase && text.equalsIgnoreCase(from))
            || (!ignoreCase && text === from)
        ) {
            $el.text(to);
        }
    });
};

const checkClientName = (clientNameToCheck, clientNameExpected) => {
    let urlEncoded = clientNameExpected.replaceAll(' ', '%20');

    return clientNameToCheck === clientNameExpected
        || clientNameToCheck === urlEncoded;
};

const compareQueryParams = (urlParamsToUse, paramName, compareValue, ignoreCase = false) => {
    let queryValue;

    // If url params not provided get them from window object
    if(isUndefinedOrNull(urlParamsToUse)) {
        urlParamsToUse = urlParams;
    }

    // Make sure param name was provided
    if(isUndefinedOrNull(paramName)){
        throw new ReferenceError('query param name must be provided')
    }

    // Get param value
    queryValue = urlParamsToUse.get(paramName);

    // If query param missing then check to see if this is expected (read: compareValue is also null)
    if(isUndefinedOrNull(queryValue)){
        return queryValue === compareValue;
    }

    // If query parm present then compare based on comparison type
    if(ignoreCase){
        return queryValue.equalsIgnoreCase(compareValue);
    }

    return queryValue === compareValue;
};

const convertIntFlagToBoolean = (value) => value === '1';

const generateUUID = () => {
    let d, d2;
    if(self && self.crypto && self.crypto.randomUUID) {
        return self.crypto.randomUUID();
    }

    // Public Domain/MIT

    // Timestamp
    d = new Date().getTime();

    // Time in microseconds since page-load or 0 if unsupported
    d2 = (performance && performance.now && (performance.now()*1000)) || 0;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
        /[xy]/g,
        function(c) {
            // random number between 0 and 16
            let r = Math.random() * 16;
            // Use timestamp until depleted
            if (d > 0) {
                r = (d + r) % 16 | 0;
                d = Math.floor(d / 16);
            } else {
                // Use microseconds since page-load if supported
                r = (d2 + r) % 16 | 0;
                d2 = Math.floor(d2 / 16);
            }

            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
};

const getProductHandleFromUrl = (path) => {
    let retVal;

    retVal = '';
    if (path.toLowerCase().indexOf('/products') >= 0) {
        let handleToEnd = path.substring("/products".length + 1);
        let split = handleToEnd.split('/');
        retVal = split[0];
    }

    ontech.log.debug(`handle - '${retVal}'`);
    return retVal;
};


const isBoolean = (potentialBoolean) => {
    if(typeof potentialBoolean === 'boolean'){
        return true;
    }

    if(typeof potentialBoolean === 'string'){
        return potentialBoolean.equalsIgnoreCase('true')
            || potentialBoolean.equalsIgnoreCase('false');
    }

    return false;
};

const isCartItemBundle = (cartItem) =>{
    if(!isUndefinedOrNull(cartItem)){
        return cartItem.product_type === ProductTypes.BUNDLE;
    }

    return false;
}

const isCartItemMembership = (cartItem) =>{
    if(!isUndefinedOrNull(cartItem)){
        return cartItem.product_type === ProductTypes.MEMBERSHIP;
    }

    return false;
}

const isCartItemProduct = (cartItem) =>{
    if(!isUndefinedOrNull(cartItem)){
        return cartItem.product_type === ProductTypes.PRODUCT;
    }

    return false;
}

const isCartItemService = (cartItem) =>{
    let productType;
    if(!isUndefinedOrNull(cartItem)){
        productType = cartItem.productType || cartItem.product_type;
        return productType === ProductTypes.INSTALL || productType === ProductTypes.SERVICE;
    } else {
        throw new TypeError('cartItem cannot be undefined');
    }
}

const isEmpty = (obj, whiteSpaceIsEmpty = false) => {
    if(isUndefinedOrNull(obj)){
        return true;
    }
    // Empty Array
    if(Array.isArray(obj)){
        return obj.length === 0;
    }

    // Regular Object with no properties
    return (obj.constructor === Object && Object.entries(obj).length === 0)
           // Object with property of length and set to 0
        || (typeof obj === 'object' && obj.hasOwnProperty('length') && obj.length === 0)
        // Object is jQuery Object and is empty
        || (obj instanceof jQuery && obj.length === 0)
           // Empty string
        || (!whiteSpaceIsEmpty && obj === '')
        || (whiteSpaceIsEmpty && obj.trim() === '');
};

const isEmptyParam = (paramValue) => {
    return isEmpty(paramValue, true) || paramValue === 'null';
};

const isFunction = obj => {
    return !isUndefinedOrNull(obj) && typeof obj === 'function'
};

const isNumber = (potentialNumber) => {
    return !isEmpty(potentialNumber) && !isNaN(potentialNumber);
};

const isUndefinedOrNull = obj => {
    return obj === undefined || obj === null;
}

const listIncludesIgnoreCase = (list, stringToSearch, trim = false) => {
    for (let value of list) {
        value = trim === true ? value.trim() : value;
        if(value.equalsIgnoreCase(stringToSearch)){
            return true;
        }
    }

    return false;
};

const safeTrim = function (val) {
    return isEmpty(val) ? val : val.trim();
};

const selfOrDefault = (self, defaultValue, replaceOnEmpty = false) => {
    if(replaceOnEmpty && isEmpty(self)){
        return defaultValue;
    }

    if(isUndefinedOrNull(self)){
        return defaultValue;
    }

    return self;
};

/**
 * This method is used for performing a logical XOR operation on two elements.
 * This is needed because JavaScript only provides a built-in bit-wise XOR
 * @param {Object} a
 * @param {Object} b
 * @returns {boolean}
 * @constructor
 */
const XOR = (a, b) => {
    return ((a && !b) || (!a && b));
}

/**
 * This method will check if object is Array, if not convert it to array. If value is undefined or
 * null then empty list is returned.
 * @param potentialArray Object to check if Array
 * @returns {*[]|*} Array
 */
const toArray = (potentialArray) => {
    if(Array.isArray(potentialArray)){
        return potentialArray;
    }

    return isUndefinedOrNull(potentialArray) ? [] : [potentialArray];
}

/**
 * Will convert value to boolean
 * @param booleanString
 * @returns {boolean|*}
 */
const toBoolean = (booleanString) => {
    if(isUndefinedOrNull(booleanString)){
        return false;
    }

    if(isBoolean(booleanString) && typeof booleanString !== 'string') {
        return booleanString;
    }

    return booleanString.equalsIgnoreCase('true');
};

const toFloat = (potentialNumber, defaultValue) => {
    if(!isNumber(potentialNumber)){
        return !isUndefinedOrNull(defaultValue) ? defaultValue : 0.0;
    }

    return (typeof potentialNumber === 'number') ? potentialNumber : parseFloat(potentialNumber);
};

const toInteger = (potentialNumber, defaultValue) => {
    if(!isNumber(potentialNumber)){
        return defaultValue !== undefined ? defaultValue : 0;
    }

    return (typeof potentialNumber === 'number') ? potentialNumber : parseInt(potentialNumber, 10);
};

const uncheckElement = (element, triggerChange = false) => {
    let $el = $(element);
    if($el.length > 0){
        $el.prop('checked', false);
        if(triggerChange){
            $el.change();
        }
    }
};

const urlParams = new URLSearchParams(window.location.search);

const validateAddressLine = addressLine => {
    if(!isEmpty(addressLine) && addressLine.length > 64){
        return 'Address (' + addressLine +
            ') exceeds the maximum limit of 64 characters. </br>  Please amend your address and try again.';
    }

    return null;
};

const validateAddressLines = addressLines => {
    let validationString = null;

    if(isEmpty(addressLines)){
        return null;
    }

    if(!Array.isArray(addressLines)){
        addressLines = [addressLines];
    }

    for(const addressLine of addressLines){
        let v = validateAddressLine(addressLine);
        if(!isUndefinedOrNull(v)){
            if(isUndefinedOrNull(validationString)){
                validationString = v;
            }
            else {
                validationString += '<br/>' + v;
            }
        }
    }

    return validationString;
};

const validateJquery = () => {
    // Did jQuery get replaced?
    if(window.fullJQuery.fn.jquery !== $.fn.jquery){
        ontech.log.debug(
            'Replacing jQuery ' + $.fn.jquery + ' with ' + window.fullJQuery.fn.jquery);
        // Restore jQuery
        $.noConflict(true);
        window.$ = window.jQuery = window.fullJQuery;
        ontech.log.debug('Current jQuery version is ' +  $.fn.jquery);
    }
};

// Start - Check Customer Device Type - Mobile, Tab or Desktop
function detectCustomerDeviceType() {
    const deviceType = new UAParser(navigator.userAgent).getDevice().type;
    switch (deviceType) {
        case "tablet":
        case "mobile":
            return deviceType;
        default:
            return "desktop";
    }
}

/*===== remove cookies on logout ======*/
async function eraseCookiesOnLogout() {
    ontech.removeCookie(CookiesConstants.JWT_TOKEN);
    ontech.state.do(State.Actions.CUSTOMER_CHANGED, {'emailAddress':null});
    ontech.removeCookie(CookiesConstants.CUSTOMER_ID);
    ontech.state.do(State.Actions.AGENT_CHANGED, null);
    ontech.removeCookie(CookiesConstants.RANDOM_NUMBER_CALL);
    ontech.removeCookie(CookiesConstants.RNG_URL);

    // set a cookie to redirect Google Profinder users
    // back to pages/nest on logout.
    if (ontech.state.get(State.Partner.ID) === ClientNames.D2C_GOOGLE_NEST) {
        ontech.setCookie(CookiesConstants.PROFINDER_LOGOUT, 'true');
    }

    ontech.state.do(State.Actions.LOGOUT);

    await UpdateCartMembershipBenefits();
}

function isShaFlow(){
    return toBoolean(ontech.getCookie(CookiesConstants.SHA_ACTIVE));
}

function isOSSorAgentFlow(){
    return isOssFlow() || ontech.state.get(State.AGENT_ID) || ontech.state.get(State.TECH_ID);
}

function isOssFlow() {
    if (!isEmpty(ontech.state.get(State.ACTION)) && !isEmpty(ontech.state.get(State.CLIENT_NAME))){
        return ontech.state.get(State.ACTION).includes(Actions.OSS) || OSS_CLIENT_NAMES.includes(ontech.state.get(State.CLIENT_NAME));
    }
    return false;
}

async function getNoteAttributes(cart){
    let cartContainsQualifyingProduct, refundCode, ogOptIns, ogOfferIds;

    if(isEmpty(cart)) {
        cart = await ontech.getCart('getNoteAttributes');
    }
    cartContainsQualifyingProduct = await cartContainsQualifyingGoogleShaProduct(cart);
   
    refundCode = null;
    if (
        toBoolean(ontech.getCookie(CookiesConstants.SHA_ACTIVE))
        && !ontech.state.get(State.Customer.SHA_REFUND)
        && cartContainsQualifyingProduct
    ) {
        refundCode = RefundCodes.SHA_DISCOUNT;
        ontech.state.do('onShaCustomerDiscountChanged', true); // this is set when user logs in
        ontech.logEvent(`Writing Refund Code "${RefundCodes.SHA_DISCOUNT}"`);
    }
    ogOptIns = null, ogOfferIds = null;
  	if (isMonthlyMembershipInCart(cart)) {
  		let ogData = getOrderGrooveData()
  		ogOptIns = ogData.optIns;
            ogOfferIds = ogData.offerIds;
    }
    return {
        [NoteAttribute.CLIENT_NAME]: ontech.state.get(State.CLIENT_NAME),
        [NoteAttribute.TECH_ID]: ontech.state.get(State.TECH_ID),
        [NoteAttribute.AGENT_ID]: ontech.state.get(State.AGENT_ID),
        [NoteAttribute.PARENT]: ontech.state.get(State.ParentOrder.NUMBER),
        [NoteAttribute.PARENT_WORK_ORDER]: ontech.state.get(State.ParentOrder.WORK_ORDER),
        [NoteAttribute.PARTNER_ID]: ontech.state.get(State.Partner.ID),
        [NoteAttribute.MEMBERSHIP]: ontech.state.get(State.Membership.STATUS),
        [NoteAttribute.LOGGED_IN]: ontech.state.get(State.Customer.IS_LOGGED_IN),
        [NoteAttribute.MEMBER_FREE_INSTALL_TAG]: null,
        [NoteAttribute._20_PERCENT_OFF_SELECT_ITEMS]: null,
        [NoteAttribute.ROLLBAR_SESSION_ID]: ontech.getSessionId(),
        [NoteAttribute.REFUND_CODE]: refundCode,
        [NoteAttribute.TECH_DELIVERED_FLAG]: ontech.state.get(State.Oss.TECH_DELIVERED),
        [NoteAttribute.OG_OFFER_IDS]: ogOfferIds,
        [NoteAttribute.OG_OPTINS]: ogOptIns,
        
        //Third party attributes
        [NoteAttribute.Partners.ADT.Third_Party.OE]: ontech.state.get(State.Adt.Third_Party.OE),
        [NoteAttribute.Partners.ADT.Third_Party.AGENT_ID]: ontech.state.get(State.Adt.Third_Party.AGENT)
    };
}

function timeout(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

// Check if url exists synchronously
function urlExists(url) {
    let http = $.ajax({
        type: "HEAD",
        url: url,
        async: false,
    });

    return http.status;
}

// TODO replace references of this for waitForElement()
function waitForElm(selector) {
    return new Promise(resolve => {
        if (document.querySelector(selector)) {
            return resolve(document.querySelector(selector));
        }

        const observer = new MutationObserver(mutations => {
            if (document.querySelector(selector)) {
                resolve(document.querySelector(selector));
                observer.disconnect();
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    });
}

// Check if cartItem includes a specific VariantId 
const isItemInCart = async (varId) => {
    let isItemInCart = false;
    const cart = await ontech.getCart();
    for (const item of cart.items) {
        if (varId === item.variant_id) {
            isItemInCart = true;
        }
    }
    return isItemInCart;
}